import axios from 'axios'
import { message as antdMessage } from 'ant-design-vue'
import type { AxiosInstance, InternalAxiosRequestConfig, AxiosResponse, AxiosRequestConfig } from 'axios'

// 基础响应格式
export interface ApiResponse<T = unknown> {
    success: boolean      // 请求是否成功
    code: number         // HTTP状态码
    message: string      // 响应消息
    data: T             // 实际业务数据
    timestamp: string   // 响应时间戳，ISO格式
}

// 分页数据结构 - 简化版
export interface PaginationData<T> {
    items: T[]        // 数据列表
    page: number      // 当前页码  
    size: number      // 每页大小
    total: number     // 总记录数
    has_more: boolean // 是否有更多数据
}

// 基础配置
// 避免硬编码IP，生产环境应通过 VITE_API_URL 指定；若未设置则退回空字符串由相对路径代理处理
const normalizeApiBaseUrl = (value: unknown): string => {
    const raw = typeof value === 'string' ? value.trim() : ''
    if (!raw) return ''

    // All API modules already pass paths like /api/auth/login-password/.
    // If production config is set to /api or https://host/api, strip that tail
    // so requests do not become /api/api/...
    return raw.replace(/\/api\/?$/i, '').replace(/\/+$/, '')
}

const BASE_URL = normalizeApiBaseUrl(import.meta.env.VITE_API_URL || '/connect')
const USE_BROWSER_API_ALIAS = /\/(?:backend-api|gateway|connect)$/i.test(BASE_URL)
const TIMEOUT = 15000

const toLogicalApiUrl = (url: string): string => {
    if (!USE_BROWSER_API_ALIAS || !url || /^https?:\/\//i.test(url) || url.startsWith('/api/')) {
        return url
    }

    return `/api${url.startsWith('/') ? '' : '/'}${url}`
}

const toBrowserApiUrl = (url: string): string => {
    if (!USE_BROWSER_API_ALIAS || !url || /^https?:\/\//i.test(url)) {
        return url
    }

    return url.replace(/^\/api(?=\/|$)/i, '') || '/'
}

// 创建axios实例
const request: AxiosInstance = axios.create({
    baseURL: BASE_URL,
    timeout: TIMEOUT,
    headers: {
        'Content-Type': 'application/json',
    },
})

// 导入userStore的函数，用于获取token（延迟导入避免循环依赖）
let getUserStore: (() => unknown) | undefined

// 请求计数器
let requestCounter = 0

// 请求黑名单 - 这些请求不显示成功提示
const SILENT_REQUEST_BLACKLIST = [
    'check-payment-status',
    'recharge-status',  // 钱包充值状态轮询
    'notifications/unread-count',  // 通知未读数量轮询
    'notifications/unread',  // 通知未读列表
    '/api/services/notifications/',  // 首页公告未登录时静默降级为空
    '/api/market/',
    '/api/ranks/',
    '/api/skins/',
    '/api/gun-skins/',
    '/api/operator-skins/',
    '/api/system-settings/',
    '/api/services/home-banners/',
    '/api/files/decode-qr/',
    '/pre-validate/',
    '/api/services/support/',
    '/api/admin/services/support/',
    '/api/wallet/withdrawal-requests/',
    '/api/auth/login-password/',
    '/api/auth/login-sms/',
    '/api/auth/send-code/'
]

const PUBLIC_AUTH_PATHS = [
    '/api/auth/login-password/',
    '/api/auth/login-sms/',
    '/api/auth/send-code/',
    '/api/auth/reset-password/'
]

const PUBLIC_BROWSE_PATH_PREFIXES = [
    '/api/market/',
    '/api/ranks/',
    '/api/skins/',
    '/api/gun-skins/',
    '/api/operator-skins/',
    '/api/system-settings/consumption-rules/',
    '/api/system-settings/platform-config/',
    '/api/system-settings/publish-prompts/active/'
]

const shouldSkipAuthHeader = (config: InternalAxiosRequestConfig): boolean => {
    const requestUrl = toLogicalApiUrl(config.url || '')
    if (PUBLIC_AUTH_PATHS.some(path => requestUrl.includes(path))) return true

    const method = (config.method || 'get').toLowerCase()
    if (method !== 'get') return false

    return PUBLIC_BROWSE_PATH_PREFIXES.some(path => requestUrl.includes(path))
}

// 请求拦截器
request.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
        // 增加请求计数并打印
        requestCounter++
        console.log(`🟢 请求 #${requestCounter}: ${config.method?.toUpperCase()} ${config.url}`)

        // 获取JWT认证token
        const token = shouldSkipAuthHeader(config) ? null : localStorage.getItem('access_token')
        if (token && config.headers) {
            config.headers.Authorization = `Bearer ${token}`
        }

        config.url = toBrowserApiUrl(toLogicalApiUrl(config.url || ''))

        // 自动检测并转换包含文件的数据为FormData
        if (config.data && !(config.data instanceof FormData) && typeof config.data === 'object') {
            const hasFile = Object.values(config.data).some(value => value instanceof File)

            if (hasFile) {
                console.log('📤 检测到文件数据，自动转换为FormData')
                const formData = new FormData()

                // 将所有字段添加到FormData中
                Object.entries(config.data).forEach(([key, value]) => {
                    if (value !== undefined && value !== null) {
                        if (value instanceof File) {
                            formData.append(key, value)
                        } else if (Array.isArray(value)) {
                            // 处理数组字段
                            value.forEach(item => formData.append(key, String(item)))
                        } else {
                            formData.append(key, String(value))
                        }
                    }
                })

                config.data = formData
            }
        }

        // 检测FormData并自动处理Content-Type
        if (config.data instanceof FormData && config.headers) {
            // 删除Content-Type头，让浏览器自动设置正确的multipart/form-data和boundary
            delete config.headers['Content-Type']
            console.log('📤 使用FormData格式，已自动删除Content-Type头')
        }

        // 对于静态数据（段位、皮肤等）允许缓存，其他数据防止缓存
        if (config.method === 'get' && !config.url?.includes('game-ranks') && !config.url?.includes('game-skins')) {
            config.params = {
                ...config.params,
                _t: Date.now()
            }
        }

        return config
    },
    (error) => {
        console.error('🔴 请求拦截器错误:', error)
        return Promise.reject(error)
    }
)

// 响应拦截器
request.interceptors.response.use(
    (response: AxiosResponse) => {
        const { data, status, config } = response

        // 检查当前请求是否在黑名单中
        const requestUrl = toLogicalApiUrl(config?.url || '')
        const isSilentRequest = SILENT_REQUEST_BLACKLIST.some(pattern => requestUrl.includes(pattern))

        // 统一处理成功响应
        if (status === 200 || status === 201 || status === 204) {
            // 检查是否是统一响应格式
            if (data && typeof data === 'object' && 'success' in data) {
                if (data.success) {
                    // 成功时，若后端提供 message 且不在黑名单中，则弹出提示
                    const successMsgMaybe = (data as { message?: unknown }).message
                    if (typeof successMsgMaybe === 'string' && successMsgMaybe && !isSilentRequest) {
                        antdMessage.success(successMsgMaybe)
                    }
                    // 直接返回业务数据
                    return data.data
                } else {
                    const errorMsg = data.message || '请求失败'
                    // 失败时弹出错误提示
                    antdMessage.error(errorMsg)
                    console.error('🔴 API响应错误:', errorMsg)
                    return Promise.reject(new Error(errorMsg))
                }
            }
            // 非统一格式：若包含 message 且不在黑名单中，也进行成功通知
            if (data && typeof data === 'object' && !isSilentRequest) {
                const msgMaybe = (data as { message?: unknown }).message
                if (typeof msgMaybe === 'string' && msgMaybe) {
                    antdMessage.success(msgMaybe)
                }
            }
            return data
        }

        return response
    },
    (error) => {
        const { response, config } = error
        const requestUrl = toLogicalApiUrl(config?.url || '')
        const isSilentRequest = SILENT_REQUEST_BLACKLIST.some(pattern => requestUrl.includes(pattern))

        if (axios.isCancel(error) || error?.code === 'ERR_CANCELED') {
            return Promise.reject(error)
        }

        console.error('🔴 响应错误:', {
            url: config?.url,
            status: response?.status,
            data: response?.data
        })

        // 统一错误处理
        if (response) {
            const { status, data } = response
            let errorMessage = '请求失败'
            const requestAuthHeader = config?.headers?.Authorization
            const requestToken = typeof requestAuthHeader === 'string'
                ? requestAuthHeader.replace(/^Bearer\s+/i, '')
                : ''
            const currentToken = localStorage.getItem('access_token') || ''
            const requestUsedCurrentToken = Boolean(requestToken && requestToken === currentToken)

            // 简化逻辑：优先使用后端返回的 message；当 message 为通用文案时，尝试提取字段级错误；否则使用通用文案
            if (data && typeof data === 'object') {
                const msgMaybe = (data as { message?: unknown }).message
                if (typeof msgMaybe === 'string' && msgMaybe) {
                    errorMessage = msgMaybe
                }

                const extractFirstFieldError = (errMaybe: unknown): string | null => {
                    if (typeof errMaybe === 'string' && errMaybe) {
                        return errMaybe
                    }
                    if (!errMaybe || typeof errMaybe !== 'object') {
                        return null
                    }

                    for (const value of Object.values(errMaybe as Record<string, unknown>)) {
                        if (typeof value === 'string' && value) {
                            return value
                        }
                        if (Array.isArray(value) && value.length > 0) {
                            const first = value[0]
                            if (typeof first === 'string' && first) {
                                return first
                            }
                        }
                    }
                    return null
                }

                const fieldError = extractFirstFieldError((data as { errors?: unknown }).errors)
                    || extractFirstFieldError((data as { error?: unknown }).error)

                if (fieldError && ['验证失败', '请求失败', '操作失败'].includes(errorMessage)) {
                    errorMessage = fieldError
                } else if (fieldError && !errorMessage) {
                    errorMessage = fieldError
                }
            }

            // 特殊处理401认证错误
            if (status === 401) {
                // Public/background requests can legitimately return 401. Only a
                // request that actually carried the current token may expire it.
                if (requestUsedCurrentToken) {
                    localStorage.removeItem('access_token')
                    localStorage.removeItem('user')

                    try {
                        if (!getUserStore) {
                            import('@/stores').then(storeModule => {
                                const { useAuthStore } = storeModule
                                const authStore = useAuthStore()
                                authStore.clearUserAuth()
                            })
                        }
                    } catch (error) {
                        console.warn('⚠️ 清除认证状态失败:', error)
                    }

                    if (!isSilentRequest) {
                        window.dispatchEvent(new CustomEvent('auth-expired', {
                            detail: { message: errorMessage }
                        }))
                    }
                }

                // 401 也弹出错误提示
                if (!isSilentRequest) {
                    antdMessage.error(errorMessage)
                }

            } else {
                // 其他错误统一弹出错误提示
                if (!isSilentRequest) {
                    antdMessage.error(errorMessage)
                }
                console.error(errorMessage)
            }

            // 返回标准化的错误对象
            const errorObj = {
                message: errorMessage,
                status: status,
                response: {
                    data: data,
                    status: status
                }
            }

            console.error('❌ 标准化错误对象:', errorObj)
            return Promise.reject(errorObj)

        } else {
            const isTimeout = error?.code === 'ECONNABORTED' || error?.code === 'ETIMEDOUT'
            const errorMessage = isTimeout ? '请求超时，请稍后重试' : '网络错误，请检查网络连接'
            if (!isSilentRequest) {
                antdMessage.error({
                    content: errorMessage,
                    key: 'network-request-error',
                    duration: 3
                })
            }
            console.error('❌ 网络错误:', error.message)
            return Promise.reject({
                message: errorMessage,
                status: 0,
                isNetworkError: !isTimeout,
                isTimeout
            })
        }
    }
)

// 语义化的请求方法
export const http = {
    // 获取列表数据（分页）
    list<T = unknown>(url: string, params?: unknown): Promise<PaginationData<T>> {
        return request.get(url, { params })
    },

    // 获取单个资源详情
    get<T = unknown>(url: string, config?: { params?: unknown; timeout?: number }): Promise<T> {
        return request.get(url, config)
    },

    // 创建资源 + 自定义操作（包含文件上传）
    post<T = unknown>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> {
        return request.post(url, data, config)
    },

    // 更新资源
    put<T = unknown>(url: string, data?: unknown): Promise<T> {
        return request.put(url, data)
    },

    // 删除资源
    delete<T = unknown>(url: string): Promise<T> {
        return request.delete(url)
    }
}

export default request 
